import { TrashIcon } from '@phosphor-icons/react';
import { Header } from '@/components/Header';
import { EventDetailsSection } from '@/components/forms/EventDetailsSection';
import { EventMaterialsSection } from '@/components/forms/EventMaterialsSection';
import { RsvpAttendanceSection } from '@/components/forms/RsvpAttendanceSection';
import { ActionDrawer } from '@/components/ui/action-drawer';
import { ToastNotification } from '@/components/ui/ToastNotification';
import type { EventFormProps } from '@/lib/types/events.types';
import { useEventForm } from '@/lib/hooks/useEventForm';
import { useRoleDirectory } from '@/lib/hooks/useRoleDirectory';
import { EditEventContent } from '@/content/EditEventContent';
import { getInvitationGroupChips } from '@/lib/utils/invitation-groups.utils';

export function EventForm({ mode, eventId }: EventFormProps) {
  useRoleDirectory();
  const {
    form,
    isEditMode,
    pageTitle,
    isLoadingEvent,
    isLoadingEventFiles,
    drawerOpen,
    setDrawerOpen,
    sendNotification,
    setSendNotification,
    notificationMessage,
    setNotificationMessage,
    handleConfirmUpdate,
    isUpdating,
    deleteDrawerOpen,
    setDeleteDrawerOpen,
    handleConfirmDelete,
    isDeleting,
    submitAttempted,
    validationErrors,
    materialFiles,
    materialErrors,
    recurrenceCount,
    isRecurringCreate,
    addMaterialFiles,
    updateMaterialTitle,
    removeMaterialFile,
    restoreMaterialFile,
    toggleMaterialOccurrence,
    toast,
    hideToast,
  } = useEventForm({
    mode,
    eventId,
  });
  const invitationGroupChips = getInvitationGroupChips(
    form.state.values.invitationGroups
      ? Array.from(form.state.values.invitationGroups)
      : null
  );

  const drawerContent = isEditMode
    ? EditEventContent.updateDrawer
    : EditEventContent.publishDrawer;

  // Show loading state while event data is being fetched in edit mode
  if (isLoadingEvent || isLoadingEventFiles) {
    return (
      <div className="flex flex-col">
        <Header enableBack />
        <div className="w-full max-w-4xl mx-auto px-4 pb-12">
          <div className="flex justify-center items-center py-12">
            <p className="text-body text-foreground/60">Loading event...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Publish/Update Event Drawer */}
      <ActionDrawer
        open={drawerOpen}
        onOpenChange={(open) => {
          setDrawerOpen(open);
          if (!open) {
            setSendNotification(false);
            setNotificationMessage('');
          }
        }}
        title={drawerContent.title}
        description={drawerContent.message}
        confirmLabel={
          isEditMode
            ? (drawerContent as typeof EditEventContent.updateDrawer)
                .updateButton
            : (drawerContent as typeof EditEventContent.publishDrawer)
                .publishButton
        }
        cancelLabel={drawerContent.cancelButton}
        onConfirm={handleConfirmUpdate}
        isLoading={isUpdating}
        loadingLabel="Updating..."
      >
        {/* Selected invitation groups chips */}
        {invitationGroupChips.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {invitationGroupChips.map((chip) => (
              <span
                key={chip.role}
                className={`px-3 py-1 text-small font-medium rounded-[6px] border ${chip.className}`}
              >
                {chip.label}
              </span>
            ))}
          </div>
        )}
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={sendNotification}
            onChange={(e) => setSendNotification(e.target.checked)}
            className="w-5 h-5 bg-slate-600 border border-slate-400 rounded text-purple-400 focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 focus:ring-offset-slate-700 cursor-pointer"
          />
          <span className="text-body text-slate-50">
            {drawerContent.sendNotification}
          </span>
        </label>
        {sendNotification && (
          <textarea
            value={notificationMessage}
            onChange={(e) => setNotificationMessage(e.target.value)}
            placeholder={drawerContent.notificationMessagePlaceholder}
            rows={4}
            className="w-full bg-slate-600 border border-slate-400 rounded-lg px-4 py-3 text-body text-slate-50 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 resize-none"
          />
        )}
      </ActionDrawer>

      {/* Delete Event Drawer */}
      <ActionDrawer
        open={deleteDrawerOpen}
        onOpenChange={setDeleteDrawerOpen}
        title={EditEventContent.deleteDrawer.title}
        description={EditEventContent.deleteDrawer.message}
        confirmLabel={EditEventContent.deleteDrawer.deleteButton}
        cancelLabel={EditEventContent.deleteDrawer.cancelButton}
        confirmVariant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        loadingLabel="Deleting..."
      />

      <div className="flex flex-col">
        <Header enableBack />
        <div className="w-full max-w-4xl mx-auto px-4">
          <div className="mb-2">
            <h1>{pageTitle}</h1>
            <div className="h-[1px] bg-[#F9F8F8]/15 border-0" />
          </div>

          <form
            id="event-form"
            className="space-y-6"
            onSubmit={(event) => {
              event.preventDefault();
              void form.handleSubmit();
            }}
          >
            <EventDetailsSection
              form={form}
              submitAttempted={submitAttempted}
              validationErrors={validationErrors}
              isEditMode={isEditMode}
            />
            <EventMaterialsSection
              files={materialFiles}
              recurrenceCount={recurrenceCount}
              isRecurringCreate={isRecurringCreate}
              isLoading={isUpdating}
              errors={materialErrors}
              onAddFiles={addMaterialFiles}
              onTitleChange={updateMaterialTitle}
              onRemove={removeMaterialFile}
              onRestore={restoreMaterialFile}
              onToggleOccurrence={toggleMaterialOccurrence}
            />
            <RsvpAttendanceSection
              form={form}
              submitAttempted={submitAttempted}
              validationErrors={validationErrors}
            />
          </form>

          {/* Delete Event Button - only show in edit mode */}
          {isEditMode && (
            <div className="flex justify-center my-8">
              <button
                type="button"
                onClick={() => setDeleteDrawerOpen(true)}
                className="flex items-center gap-2 text-red-500 hover:text-red-400 transition-colors "
              >
                <TrashIcon size={20} weight="regular" />
                <span className="text-body">
                  {EditEventContent.deleteEvent}
                </span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      <ToastNotification
        open={toast.open}
        onClose={hideToast}
        title={toast.title}
        description={toast.description}
        variant={toast.variant}
        position="bottom"
      />
    </>
  );
}
